"use client";

import { Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { getClientRegion, getRegionLocaleHref } from "@/services/region";
import { useTranslations, useLocale } from "next-intl";
import Link from "next/link";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import FormInput from "@/components/FormInput";
import LottieLoader from "@/components/LottieLoader";
import { isValidSignupEmail, suggestEmailCorrection } from "@/lib/emailSuggestion";
import { useRegister } from "../hooks/useRegister";
import GoogleSignInButton, { isGoogleSignInEnabled } from "./GoogleSignInButton";
import { getSafeRedirectParam, forwardReturnPath } from "../utils/loginRedirect";
import { setPostLoginRedirect } from "../utils/postLoginRedirect";
import { FREE_TRIAL_PATH, isFreeTrialPath } from "../utils/profileCompletion";
import { useRegisterMetadata } from "../hooks/useRegisterMetadata";
import PasswordRequirements from "./PasswordRequirements";
import SiteLogo from "@/components/SiteLogo";
import { useSearchParams } from "next/navigation";
import { ENGLISH_NAME_REGEX, splitFullName, withEnglishNameFilter } from "@/lib/name";

// reCAPTCHA v3 token retrieval, hardened against a torn-down grecaptcha.
//
// GoogleReCaptchaProvider's cleanup wipes `window.___grecaptcha_cfg` and
// removes the injected api.js while leaving `window.grecaptcha` in place. The
// surviving object still exposes execute(), but the site key is no longer
// registered with it, so the NEXT call throws synchronously ("Invalid site
// key or not loaded in api.js") - which is why a first login attempt worked
// and every one after it failed instantly with no request sent.
//
// So: try the provider's own executeRecaptcha first (the happy path, and the
// only one that runs when nothing is broken); if it throws or stalls, rebuild
// the script from scratch and execute against the fresh instance.
const RECAPTCHA_TIMEOUT_MS = 10_000;
const RECAPTCHA_SCRIPT_ID = "google-recaptcha-v3";

type Grecaptcha = {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

function getGrecaptcha(): Grecaptcha | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha;
}

// grecaptcha.execute() can also never settle once the instance is stale, so
// every call is bounded - a dead promise must not deadlock the submit.
function withTimeout(promise: Promise<string>): Promise<string> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  return Promise.race([
    promise,
    new Promise<string>((_, reject) => {
      timer = setTimeout(
        () => reject(new Error("recaptcha-timeout")),
        RECAPTCHA_TIMEOUT_MS,
      );
    }),
  ]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

// Drops every trace of the previous load (script tags, config, the stale
// grecaptcha object) and loads api.js again, so the site key is re-registered.
async function reloadRecaptchaScript(siteKey: string): Promise<void> {
  const globals = window as unknown as {
    ___grecaptcha_cfg?: unknown;
    grecaptcha?: unknown;
  };

  document.getElementById(RECAPTCHA_SCRIPT_ID)?.remove();
  document
    .querySelector('script[src^="https://www.gstatic.com/recaptcha/releases"]')
    ?.remove();
  globals.___grecaptcha_cfg = undefined;
  globals.grecaptcha = undefined;

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("recaptcha-script-failed"));
    document.head.appendChild(script);
  });

  await new Promise<void>((resolve) => {
    const grecaptcha = getGrecaptcha();
    if (!grecaptcha) {
      resolve();
      return;
    }
    grecaptcha.ready(resolve);
  });
}

async function requestRecaptchaToken(
  executeRecaptcha: (action: string) => Promise<string>,
  action: string,
): Promise<string> {
  try {
    // Throws synchronously when the instance is stale - caught here because
    // this function is async.
    return await withTimeout(executeRecaptcha(action));
  } catch {
    // Fall through to a rebuild rather than failing the attempt outright.
  }

  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "";
  if (!siteKey) return "";

  try {
    await reloadRecaptchaScript(siteKey);
    const grecaptcha = getGrecaptcha();
    if (!grecaptcha) return "";

    return await withTimeout(grecaptcha.execute(siteKey, { action }));
  } catch {
    return "";
  }
}

export default function RegisterForm() {
  const { handleRegister, loading, errorKey, resetError } = useRegister();
  const { errorKey: metadataErrorKey } = useRegisterMetadata();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const t = useTranslations("register");
  const tGoogle = useTranslations("auth.google");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);
  const [verificationPending, setVerificationPending] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string>("");
  const [recaptchaErrorKey, setRecaptchaErrorKey] = useState<string | null>(
    null,
  );
  const { executeRecaptcha } = useGoogleReCaptcha();



  const schema = z
    .object({
      // One box, two API values. The backend still wants fname and lname
      // separately, each 3-100 English-letter characters, so the split is
      // validated here - on the field the user can actually see.
      fullName: z
        .string()
        .trim()
        .min(1, t("required_full_name"))
        .refine((v) => splitFullName(v) !== null, t("invalid_full_name"))
        .refine((v) => {
          const parts = splitFullName(v);

          return parts === null || ENGLISH_NAME_REGEX.test(parts.fname);
        }, t("name_english_only"))
        .refine((v) => {
          const parts = splitFullName(v);

          return parts === null || ENGLISH_NAME_REGEX.test(parts.lname);
        }, t("name_english_only")),
      // Same pattern and misspelling list as the backend's SignupEmailAddress
      // rule (REQ-5): the form refuses exactly what the API would, so a
      // server-side email error reaching this form means "already taken".
      email: z
        .string()
        .trim()
        .toLowerCase()
        .email(t("invalid_email"))
        .refine(isValidSignupEmail, t("invalid_email"))
        .refine((v) => suggestEmailCorrection(v) === null, t("email_check_domain")),
      password: z
        .string()
        .min(8, t("invalid_password"))
        .refine((v) => /[a-z]/.test(v), t("invalid_password"))
        .refine((v) => /[A-Z]/.test(v), t("invalid_password"))
        .refine((v) => /[0-9]/.test(v), t("invalid_password"))
        .refine((v) => /[^a-zA-Z0-9]/.test(v), t("invalid_password")),
      password_confirmation: z
        .string()
        .min(1, t("required_password_confirmation")),
    })
    .refine((d) => d.password === d.password_confirmation, {
      message: t("passwords_mismatch"),
      path: ["password_confirmation"],
    });

  const {
    control,
    register,
    handleSubmit,
    setFocus,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: "",
      email: "",
      password: "",
      password_confirmation: "",
    },
  });

  useEffect(() => {
    const firstErrorKey = Object.keys(errors)[0] as any;
    if (firstErrorKey) {
      setFocus(firstErrorKey);
    }
  }, [errors, setFocus]);

  const onSubmit = async (data: any) => {
    // Clear BOTH banners up front - the API error lives in useRegister and
    // was reset only once the request was actually issued, so an attempt that
    // stopped before that left the previous message on screen.
    setRecaptchaErrorKey(null);
    resetError();

    // Obtain a fresh v3 token silently – falls back to empty string when the
    // provider is not configured (local dev without NEXT_PUBLIC_RECAPTCHA_SITE_KEY)
    // or disabled via NEXT_PUBLIC_RECAPTCHA_ENABLED. useGoogleReCaptcha() still
    // returns a truthy executeRecaptcha even with no provider in the tree (a
    // stub that throws when called), so the guard must check the same env
    // vars AuthLayout uses to decide whether to render GoogleReCaptchaProvider
    // at all - not just whether the function exists.
    let recaptchaToken = "";
    if (
      process.env.NEXT_PUBLIC_RECAPTCHA_ENABLED !== "false" &&
      process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY &&
      executeRecaptcha
    ) {
      recaptchaToken = await requestRecaptchaToken(executeRecaptcha, "register");

      if (!recaptchaToken) {
        setRecaptchaErrorKey("recaptcha_failed");
        return;
      }
    }

    const region = getClientRegion(pathname);

    try {
      // The schema already refused anything splitFullName() cannot handle, so
      // this cannot be null by the time submission runs.
      const name = splitFullName(data.fullName)!;

      const res = await handleRegister({
        fname: name.fname,
        lname: name.lname,
        email: data.email.trim().toLowerCase(),
        password: data.password,
        password_confirmation: data.password_confirmation,
        region,
        "g-recaptcha-response": recaptchaToken,
      });

      reset();

      // Where to land now that registration has authenticated the account:
      // always the free-trial wizard. This form collects only name, email and
      // password, so the account is created with its profile incomplete, and
      // the wizard's first step asks for the rest (phone, country / time zone,
      // birthdate...) before the trial questions. A `?redirect=` is kept only
      // when it already points at the free-trial page, for its query string;
      // sanitizeReturnPath (inside getSafeRedirectParam) is the open-redirect
      // guard. AuthLayout applies the same rule the moment `user` resolves
      // (see needsProfileCompletion), so the two navigations agree instead of
      // racing each other to different destinations.
      //
      // A brand-new account is created inactive (AuthService sets
      // is_active => false until the emailed code is entered). The free-trial
      // page's EmailVerificationGuard holds the visitor right there and opens
      // the verification modal over it, and the wizard renders in place as
      // soon as the code is accepted.
      const returnTo = getSafeRedirectParam(searchParams.toString());
      const destination = isFreeTrialPath(returnTo)
        ? returnTo!
        : getRegionLocaleHref(pathname, locale, FREE_TRIAL_PATH);

      setPostLoginRedirect(FREE_TRIAL_PATH);

      if (res && (res as any).message === "verification_sent") {
        setVerificationPending(true);
        setPendingEmail(data.email.trim().toLowerCase());
        setTimeout(() => router.replace(destination), 1500);
        return;
      }

      router.replace(destination);
    } catch {
      // v3 tokens are single-use; a new one will be generated on the next submit.
    }
  };


  const watchedPassword = useWatch({
    control,
    name: "password",
  });

  // "Did you mean ...?" - shown as the address is typed, before any submit.
  const watchedEmail = useWatch({
    control,
    name: "email",
  });
  const emailSuggestion = suggestEmailCorrection(watchedEmail ?? "");



  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-cover"
      style={{ backgroundImage: "url('/f/images/bg-sign.png')" }}
    >
      <div className="w-full max-w-2xl">
        <div className="w-full flex items-center justify-center gap-2 border-2 border-secondary bg-transparent text-secondary font-semibold mt-5 mb-5 py-2.5 px-8 rounded-[10px] transition-colors duration-200 hover:bg-secondary hover:text-white">
          {/* Carries any `?redirect=` on through to login, so a returning
              visitor who landed here from the free-trial page still gets sent
              back to it after signing in. */}
          <Link
            href={forwardReturnPath(
              getRegionLocaleHref(pathname, locale, "/login"),
              searchParams.toString(),
            )}
            className="w-full text-center"
          >
            <span>{t("has_account")}</span>

            {t("login")}
          </Link>
        </div>
      </div>
      <div className="bg-table border border-border w-full max-w-2xl rounded-2xl p-6">
        {/* Logo */}
        <div className="flex justify-center mb-5">
          <SiteLogo
            fallbackSrc="/f/images/dark-logo.png"
            width={100}
            height={88}
          />
        </div>
        {/* Title */}
        <h3 className="text-xl text-center text-primary font-semibold">
          {t("sign_up_account")}
        </h3>

        {(errorKey || metadataErrorKey || recaptchaErrorKey) && (
          <div className="bg-danger/10 text-danger p-2 my-3 rounded">
            {t(errorKey || metadataErrorKey || recaptchaErrorKey!)}
          </div>
        )}

        {verificationPending && (
          <div className="bg-success/10 text-success p-3 my-3 rounded">
            <strong className="block">{t("verification_sent_title")}</strong>
            <span>
              {t("verification_sent_description", { email: pendingEmail })}
            </span>
          </div>
        )}

        {/* Continue with Google. ?ref= is forwarded so a Google sign-up is
            still attributed to the referrer - read straight from the URL,
            since the referral input itself now lives on the second step.
            Hidden together with its "or" divider when Google sign-in is
            switched off, so no orphaned divider is left above the form. */}
        {isGoogleSignInEnabled() && (
          <>
            <div className="mt-4">
              <GoogleSignInButton
                variant="continue"
                referralCode={searchParams.get("ref")?.trim().slice(0, 16) || null}
              />
            </div>
            <div className="my-5 flex items-center gap-3">
              <span className="h-px flex-1 bg-border" />
              <span className="text-xs uppercase text-text-muted">
                {tGoogle("or_divider")}
              </span>
              <span className="h-px flex-1 bg-border" />
            </div>
          </>
        )}

        <form
          onSubmit={(e) => {
            void handleSubmit(onSubmit)(e);
          }}
          className="grid grid-cols-12 gap-3"
        >
          <div className="col-span-12 md:col-span-6">
            <FormInput
              label={t("full_name")}
              placeholder={t("enter_full_name")}
              error={errors.fullName?.message}
              register={withEnglishNameFilter(register("fullName"))}
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <FormInput
              label={t("email")}
              type="email"
              error={errors.email?.message}
              register={register("email")}
            />
            {emailSuggestion && (
              <p
                className="mt-1 text-xs text-warning"
                data-testid="email-suggestion"
                role="status"
              >
                {t("did_you_mean")}{" "}
                <button
                  type="button"
                  className="cursor-pointer font-semibold underline"
                  onClick={() =>
                    setValue("email", emailSuggestion, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                >
                  {emailSuggestion}
                </button>
                ?
              </p>
            )}
          </div>






          <div className="col-span-12 md:col-span-6">
            <FormInput
              label={t("password")}
              type={showPassword ? "text" : "password"}
              error={errors.password?.message}
              register={register("password")}
              className="pe-12"
              endAdornment={
                <button
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 end-0 flex w-12 items-center justify-center text-primary cursor-pointer"
                  onClick={() => setShowPassword((value) => !value)}
                  type="button"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              }
            />
            <div
              className={`grid transition-all duration-300 ease-out ${
                watchedPassword
                  ? "grid-rows-[1fr] opacity-100"
                  : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <PasswordRequirements password={watchedPassword ?? ""} />
              </div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-6">
            <FormInput
              label={t("password_confirmation")}
              type={showPasswordConfirmation ? "text" : "password"}
              error={errors.password_confirmation?.message}
              register={register("password_confirmation")}
              className="pe-12"
              endAdornment={
                <button
                  aria-label={
                    showPasswordConfirmation
                      ? "Hide password confirmation"
                      : "Show password confirmation"
                  }
                  className="absolute inset-y-0 end-0 flex w-12 items-center justify-center text-primary cursor-pointer"
                  onClick={() => setShowPasswordConfirmation((value) => !value)}
                  type="button"
                >
                  {showPasswordConfirmation ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              }
            />
          </div>



          <button
            type="submit"
            disabled={loading || isSubmitting}
            className="col-span-12 mt-3 bg-primary text-on-primary py-3 rounded-md cursor-pointer transition hover:bg-secondary disabled:opacity-60 "
          >
            {loading || isSubmitting ? (
              <LottieLoader centered size={18} label={t("loading")} />
            ) : (
              t("register")
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
